import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import api, { API, authToken, DateRanges, Masters } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Calendar as CalendarIcon, Download, ArrowRight, Wallet, Banknote,
  Users, Bike, MapPin, Map, Trophy, Info, ShoppingBag, ClipboardCheck,
} from "lucide-react";
import { cn } from "../lib/utils";
import AskAiSection from "./AskAiSection";

// ─────────────────────── UI Primitives ───────────────────────

const RANGE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "this_week", label: "This Week" },
  { key: "this_month", label: "This Month" },
  { key: "custom", label: "Custom" },
  { key: "all_time", label: "All Time" },
];

function rangeFor(key, custom) {
  if (key === "today") return DateRanges.today();
  if (key === "this_week") return DateRanges.thisWeek();
  if (key === "this_month") return DateRanges.thisMonth();
  if (key === "custom") {
    if (custom?.from && custom?.to) return { date_from: custom.from, date_to: custom.to };
    return DateRanges.allTime();
  }
  return DateRanges.allTime();
}

function RangeBar({ value, onChange, custom, onCustomChange }) {
  const [draftFrom, setDraftFrom] = useState(custom?.from || "");
  const [draftTo, setDraftTo] = useState(custom?.to || "");
  const showPicker = value === "custom";
  return (
    <div className="flex flex-wrap items-center gap-2">
      <CalendarIcon className="h-3.5 w-3.5 text-slate-400" />
      {RANGE_PRESETS.map((p) => (
        <button key={p.key} type="button" onClick={() => onChange(p.key)}
          className={cn(
            "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
            value === p.key ? "bg-primary text-primary-foreground" : "text-slate-600 hover:bg-slate-100",
          )}
          data-testid={`reports-range-${p.key}`}>
          {p.label}
        </button>
      ))}
      {showPicker && (
        <div className="flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2 py-1">
          <input type="date" value={draftFrom} max={draftTo || undefined}
            onChange={(e) => setDraftFrom(e.target.value)}
            className="rounded border border-slate-200 px-1.5 py-0.5 text-xs" />
          <span className="text-xs text-slate-400">→</span>
          <input type="date" value={draftTo} min={draftFrom || undefined}
            onChange={(e) => setDraftTo(e.target.value)}
            className="rounded border border-slate-200 px-1.5 py-0.5 text-xs" />
          <button type="button"
            onClick={() => (draftFrom && draftTo) && onCustomChange({ from: draftFrom, to: draftTo })}
            disabled={!draftFrom || !draftTo}
            className="rounded-md bg-primary px-2 py-0.5 text-xs font-semibold text-primary-foreground disabled:opacity-40">
            Apply
          </button>
        </div>
      )}
    </div>
  );
}

// SVG-based doughnut chart — no external chart lib to keep bundle small.
function Doughnut({ segments, size = 160, thickness = 22, center }) {
  const total = segments.reduce((s, x) => s + (x.value || 0), 0) || 1;
  const r = (size - thickness) / 2;
  const c = size / 2;
  const circumference = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle cx={c} cy={c} r={r} fill="none" stroke="#f1f5f9" strokeWidth={thickness} />
        {segments.map((s, i) => {
          const len = (s.value / total) * circumference;
          const dash = `${len} ${circumference - len}`;
          const el = (
            <circle key={i} cx={c} cy={c} r={r} fill="none"
              stroke={s.color} strokeWidth={thickness}
              strokeDasharray={dash} strokeDashoffset={-offset}
              strokeLinecap="butt" />
          );
          offset += len;
          return el;
        })}
      </svg>
      {center && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
          {center}
        </div>
      )}
    </div>
  );
}

// Horizontal bar row — reusable across finance/DSE/model/location/district reports.
function BarRow({ label, value, max, share, tone = "primary", to, testid }) {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  const tones = {
    primary: "bg-primary/80", indigo: "bg-indigo-500", emerald: "bg-emerald-500",
    amber: "bg-amber-500", rose: "bg-rose-500", sky: "bg-sky-500",
  };
  const content = (
    <div className="group grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 py-1.5"
      data-testid={testid}>
      <div className="min-w-0">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm font-medium text-slate-800 group-hover:text-primary">
            {label}
          </span>
          <span className="font-mono-data text-[11px] font-semibold text-slate-500">
            {value}{share != null && <span className="ml-1 text-slate-400">· {share}%</span>}
          </span>
        </div>
        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
          <div className={cn("h-full rounded-full transition-all", tones[tone])}
            style={{ width: `${pct}%` }} />
        </div>
      </div>
      {to && <ArrowRight className="h-3.5 w-3.5 text-slate-300 group-hover:text-primary" />}
    </div>
  );
  return to ? <Link to={to}>{content}</Link> : content;
}

function KpiTile({ label, value, subtext, icon: Icon, tone = "blue", testid, to }) {
  const tones = {
    blue: "bg-blue-50 ring-blue-100 text-blue-800",
    emerald: "bg-emerald-50 ring-emerald-100 text-emerald-800",
    amber: "bg-amber-50 ring-amber-100 text-amber-800",
    slate: "bg-slate-50 ring-slate-200 text-slate-800",
  }[tone];
  const inner = (
    <div className={cn("flex items-start gap-3 rounded-xl px-4 py-3 ring-1 transition-shadow hover:shadow-md",
      tones)} data-testid={testid}>
      <span className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-white/60">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.14em] opacity-80">{label}</div>
        <div className="mt-0.5 font-display text-3xl font-bold leading-none tracking-tight">
          {value ?? "—"}
        </div>
        {subtext && <div className="mt-1 text-[11px] opacity-75">{subtext}</div>}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

// ─────────────────────── Filter Bar ───────────────────────

function FilterBar({ locations, users, filters, setFilters, financeCompanies, modelOptions, onExport, exporting }) {
  const dseUsers = users.filter((u) => u.role === "DSE" && u.is_active !== false);
  return (
    <Card className="border-slate-200 p-3">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-8">
        <Select value={filters.location_id || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, location_id: v === "all" ? null : v }))}>
          <SelectTrigger className="h-9 text-xs" data-testid="rep-loc"><SelectValue placeholder="Location" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Locations</SelectItem>
            {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.code} — {l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.dse_id || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, dse_id: v === "all" ? null : v }))}>
          <SelectTrigger className="h-9 text-xs" data-testid="rep-dse"><SelectValue placeholder="DSE" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All DSEs</SelectItem>
            {dseUsers.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.buy_mode || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, buy_mode: v === "all" ? null : v }))}>
          <SelectTrigger className="h-9 text-xs" data-testid="rep-buymode"><SelectValue placeholder="Buy Mode" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modes</SelectItem>
            <SelectItem value="cash">Cash</SelectItem>
            <SelectItem value="finance">Finance</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.finance_company_id || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, finance_company_id: v === "all" ? null : v }))}>
          <SelectTrigger className="h-9 text-xs" data-testid="rep-fc"><SelectValue placeholder="Finance Co." /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Finance Cos</SelectItem>
            {financeCompanies.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.model || "all"}
          onValueChange={(v) => setFilters((f) => ({ ...f, model: v === "all" ? null : v }))}>
          <SelectTrigger className="h-9 text-xs" data-testid="rep-model"><SelectValue placeholder="Model" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Models</SelectItem>
            {modelOptions.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input
          placeholder="District"
          value={filters.district || ""}
          onChange={(e) => setFilters((f) => ({ ...f, district: e.target.value || null }))}
          className="h-9 text-xs"
          data-testid="rep-district"
        />
        <Input
          placeholder="State"
          value={filters.state || ""}
          onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value || null }))}
          className="h-9 text-xs"
          data-testid="rep-state"
        />
        <Button type="button" onClick={onExport} disabled={exporting}
          className="h-9 gap-1.5" data-testid="rep-export">
          <Download className="h-4 w-4" />
          {exporting ? "Exporting…" : "Export Excel"}
        </Button>
      </div>
    </Card>
  );
}

// ─────────────────────── Report Sections ───────────────────────

function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="mb-3 flex items-baseline gap-2">
      <Icon className="h-4 w-4 text-slate-500" />
      <div>
        <div className="font-display text-base font-semibold text-slate-900">{title}</div>
        {subtitle && <div className="text-[11px] text-slate-500">{subtitle}</div>}
      </div>
    </div>
  );
}

function CashVsFinance({ report, driveTo }) {
  const { total_sales, cash_total, finance_total, cash_pct, finance_pct, buy_mode_breakup } = report;
  const cashRow = buy_mode_breakup.find((r) => r.key === "cash") || {};
  const finRow  = buy_mode_breakup.find((r) => r.key === "finance") || {};
  return (
    <Card className="border-slate-200 p-5" data-testid="rep-cash-vs-finance">
      <SectionHeader icon={Wallet} title="Cash vs Finance"
        subtitle="Split of released sales between cash and finance, with delivered / pending inline." />
      <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        {/* Left: doughnut */}
        <div className="flex justify-center lg:justify-start">
          <Doughnut
            size={200} thickness={28}
            segments={[
              { value: cash_total, color: "#10b981" },
              { value: finance_total, color: "#3b82f6" },
            ]}
            center={
              <div>
                <div className="font-display text-3xl font-bold text-slate-900">{total_sales}</div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Total Sales</div>
              </div>
            }
          />
        </div>
        {/* Right: two large KPI cards with delivered/pending strip */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <CashFinanceCard
            title="Cash" total={cash_total} pct={cash_pct}
            delivered={cashRow.delivered || 0} pending={cashRow.pending || 0}
            color="emerald" icon={Wallet}
            to={driveTo({ buy_mode: "cash" })} testid="rep-kpi-cash" />
          <CashFinanceCard
            title="Finance" total={finance_total} pct={finance_pct}
            delivered={finRow.delivered || 0} pending={finRow.pending || 0}
            color="blue" icon={Banknote}
            to={driveTo({ buy_mode: "finance" })} testid="rep-kpi-finance" />
        </div>
      </div>
    </Card>
  );
}

function CashFinanceCard({ title, total, pct, delivered, pending, color, icon: Icon, to, testid }) {
  const styles = {
    emerald: { ring: "ring-emerald-200", bg: "bg-gradient-to-br from-emerald-50 to-white",
               icon: "bg-emerald-100 text-emerald-700", bar: "bg-emerald-500" },
    blue:    { ring: "ring-blue-200",    bg: "bg-gradient-to-br from-blue-50 to-white",
               icon: "bg-blue-100 text-blue-700",       bar: "bg-blue-500" },
  }[color];
  return (
    <Link to={to} data-testid={testid}
      className={cn("group flex flex-col rounded-xl p-4 ring-1 transition-all hover:-translate-y-0.5 hover:shadow-md",
        styles.ring, styles.bg)}>
      <div className="flex items-center gap-2">
        <span className={cn("flex h-9 w-9 items-center justify-center rounded-lg", styles.icon)}>
          <Icon className="h-4 w-4" />
        </span>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{title}</div>
          <div className="mt-0.5 flex items-baseline gap-1.5">
            <span className="font-display text-3xl font-bold leading-none tracking-tight text-slate-900">{total}</span>
            <span className="text-sm font-semibold text-slate-500">{pct}%</span>
          </div>
        </div>
      </div>
      {/* Delivered / Pending strip */}
      <div className="mt-4 grid grid-cols-2 gap-2 text-[11px]">
        <div className="rounded-md bg-white/70 px-2 py-1.5 ring-1 ring-slate-100">
          <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Delivered</div>
          <div className="font-mono-data text-sm font-bold text-emerald-700">{delivered}</div>
        </div>
        <div className="rounded-md bg-white/70 px-2 py-1.5 ring-1 ring-slate-100">
          <div className="text-[9px] font-bold uppercase tracking-widest text-slate-500">Pending</div>
          <div className="font-mono-data text-sm font-bold text-amber-700">{pending}</div>
        </div>
      </div>
    </Link>
  );
}

function LocationDistrictsCard({ rows, driveTo }) {
  if (!rows) return null;
  const maxTotal = rows.length ? rows[0].total : 0;
  return (
    <Card className="border-slate-200 p-5" data-testid="rep-location-districts">
      <SectionHeader icon={Map} title="Location × Districts"
        subtitle="For every dealership location, which customer districts those sales came from. Click any district to open the underlying sales list." />
      {rows.length === 0 ? (
        <div className="py-8 text-center text-xs text-slate-400">No data in this range</div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((loc) => (
            <div key={loc.location_id}
              className="flex flex-col rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
              data-testid={`rep-loc-dist-${loc.location_code}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div>
                  <div className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">
                    {loc.location_name}
                  </div>
                  <div className="text-[10px] font-semibold text-slate-500">{loc.location_code}</div>
                </div>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 font-mono-data text-[11px] font-bold text-primary">
                  {loc.total}
                </span>
              </div>
              <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-primary/70"
                  style={{ width: `${maxTotal ? Math.min(100, (loc.total / maxTotal) * 100) : 0}%` }} />
              </div>
              {/* Per-district list — scrolls internally once past ~8 rows */}
              <ul className="mt-3 max-h-[220px] space-y-1 overflow-y-auto pr-1">
                {loc.districts.map((d, i) => {
                  const dPct = loc.total ? Math.round((d.count / loc.total) * 100) : 0;
                  return (
                    <li key={`${loc.location_id}-${d.district}-${i}`}>
                      <Link to={driveTo({ district: d.district, location_id: loc.location_id })}
                        className="grid grid-cols-[minmax(0,1fr)] items-center gap-2 rounded-md px-1.5 py-1 text-xs hover:bg-slate-50"
                        data-testid={`rep-loc-dist-${loc.location_code}-${d.district}`}>
                        <div className="min-w-0">
                          <div className="flex items-baseline justify-between gap-2">
                            <span className="truncate font-medium text-slate-800">{d.district}</span>
                            <span className="font-mono-data text-[11px] font-semibold text-slate-500">
                              {d.count} <span className="text-slate-400">· {dPct}%</span>
                            </span>
                          </div>
                          <div className="mt-0.5 h-1 w-full overflow-hidden rounded-full bg-slate-100">
                            <div className="h-full rounded-full bg-primary/60"
                              style={{ width: `${dPct}%` }} />
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}


// ─── Finance Company Performance (spec: two *different* insights side by
// side — a numeric table with Total DDA on the left and a bar chart whose
// length maps to vehicles-financed with the DDA rupee value displayed under
// each row on the right. No duplicated data.) ───────────────────────────
function FinanceCompanySection({ rows, driveTo }) {
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  // Compact "22.45 Lakh" / "1.20 Cr" style — best density in the right rail.
  const inrCompact = (n) => {
    const v = Number(n || 0);
    if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
    if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)} Lakh`;
    return inr(v);
  };
  const sorted = [...(rows || [])].sort((a, b) => b.total - a.total);
  const max = sorted.length ? sorted[0].total : 0;
  const grandTotal = sorted.reduce((s, r) => s + (r.total || 0), 0);
  const grandDDA   = sorted.reduce((s, r) => s + Number(r.total_dda || 0), 0);
  return (
    <Card className="flex flex-col border-slate-200" data-testid="rep-finance-companies">
      <div className="border-b border-slate-100 px-4 pb-3 pt-4">
        <SectionHeader
          icon={Banknote}
          title="Finance Company Performance"
          subtitle="Lenders ranked by vehicles financed with total dealer disbursement contribution. Click any row to open all financed sales for that lender."
        />
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ── Table (left) — Finance Company | Sold | % Share | Total DDA */}
        <div className="max-h-[420px] overflow-y-auto border-slate-100 lg:border-r">
          <table className="min-w-full text-[13px]">
            <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                <th className="px-3 py-2 font-bold">Finance Company</th>
                <th className="px-3 py-2 text-right font-bold">Sold</th>
                <th className="px-3 py-2 text-right font-bold">% Share</th>
                <th className="px-3 py-2 text-right font-bold">Total DDA</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-3 py-8 text-center text-xs text-slate-400">No data</td>
                </tr>
              )}
              {sorted.map((r, i) => (
                <tr key={r.key || i} className="hover:bg-slate-50" data-testid={`rep-finance-companies-row-${i}`}>
                  <td className="px-3 py-1.5">
                    <Link
                      to={driveTo({ finance_company_id: r.key, buy_mode: "finance" })}
                      className="font-semibold text-slate-800 hover:text-primary"
                    >
                      {r.label}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono-data tabular-nums">{r.total}</td>
                  <td className="px-3 py-1.5 text-right font-mono-data tabular-nums">{r.share_pct}%</td>
                  <td className="px-3 py-1.5 text-right font-mono-data tabular-nums" data-testid={`rep-finance-companies-dda-${i}`}>
                    {inr(r.total_dda || 0)}
                  </td>
                </tr>
              ))}
            </tbody>
            {sorted.length > 0 && (
              <tfoot className="sticky bottom-0 z-10 bg-slate-50/95 backdrop-blur">
                <tr className="border-t-2 border-slate-200 text-[11px] font-bold uppercase tracking-widest text-slate-700"
                    data-testid="rep-finance-companies-grand-total">
                  <td className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular-nums">{grandTotal}</td>
                  <td className="px-3 py-2 text-right text-slate-400">—</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular-nums text-slate-900">{inr(grandDDA)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {/* ── Bar visualisation (right) — bar length = vehicles financed;
             the Total DDA rupee value sits under each company. */}
        <div className="max-h-[420px] space-y-3 overflow-y-auto px-4 py-3">
          {sorted.length === 0 && (
            <div className="py-8 text-center text-xs text-slate-400">No data</div>
          )}
          {sorted.map((r) => {
            const pct = max ? Math.min(100, (r.total / max) * 100) : 0;
            return (
              <Link
                key={r.key}
                to={driveTo({ finance_company_id: r.key, buy_mode: "finance" })}
                className="group block rounded-lg px-2 py-1.5 transition-colors hover:bg-slate-50"
                data-testid={`rep-finance-companies-bar-${r.key}`}
              >
                <div className="mb-1 flex items-baseline justify-between gap-2">
                  <span className="truncate font-semibold text-slate-800 group-hover:text-primary">
                    {r.label}
                  </span>
                  <span className="whitespace-nowrap font-mono-data text-[11px] font-bold text-slate-600 tabular-nums">
                    {r.total} vehicles
                  </span>
                </div>
                <div className="h-2.5 w-full overflow-hidden rounded-full bg-indigo-100/60">
                  <div className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-indigo-600"
                    style={{ width: `${pct}%` }} />
                </div>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <span className="text-[10px] font-medium uppercase tracking-wider text-slate-400">
                    Total DDA
                  </span>
                  <span className="font-mono-data text-[12px] font-bold text-emerald-700 tabular-nums">
                    {inrCompact(r.total_dda || 0)}
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </Card>
  );
}


function TableBarSection({ title, subtitle, icon, rows, columns, buildTo, tone = "primary", testid, maxBars, rankTop3 }) {  const max = rows.length ? rows[0].total : 0;
  const barSubset = (maxBars === null || maxBars === undefined) ? rows : rows.slice(0, maxBars);
  const rankStyles = {
    1: "bg-amber-100 text-amber-800 ring-amber-200",
    2: "bg-slate-100 text-slate-700 ring-slate-200",
    3: "bg-orange-100 text-orange-800 ring-orange-200",
  };
  return (
    <Card className="flex flex-col border-slate-200" data-testid={testid}>
      <div className="border-b border-slate-100 px-4 pb-3 pt-4">
        <SectionHeader icon={icon} title={title} subtitle={subtitle} />
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* Table (scrolls vertically if long) */}
        <div className="max-h-[380px] overflow-y-auto border-slate-100 lg:border-r">
          <table className="min-w-full text-[13px]">
            <thead className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur">
              <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500">
                {columns.map((c) => (
                  <th key={c.key} className={cn("px-3 py-2 font-bold", c.numeric && "text-right")}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.length === 0 && (
                <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-xs text-slate-400">No data</td></tr>
              )}
              {rows.map((r, i) => (
                <tr key={r.key || i} className="hover:bg-slate-50" data-testid={`${testid}-row-${i}`}>
                  {columns.map((c) => {
                    // Rank column: show medal chip for top 3 when opted in.
                    if (c.key === "rank" && rankTop3) {
                      return (
                        <td key={c.key} className="px-3 py-1.5 text-right">
                          <span className={cn(
                            "inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold ring-1 tabular-nums",
                            rankStyles[i + 1] || "bg-slate-50 text-slate-500 ring-slate-100",
                          )}>{i + 1}</span>
                        </td>
                      );
                    }
                    return (
                      <td key={c.key} className={cn("px-3 py-1.5",
                        c.numeric && "text-right font-mono-data tabular-nums",
                        c.mono && "font-mono-data")}>
                        {c.render ? c.render(r, i, buildTo) : r[c.key]}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Bars (scrolls vertically if long) */}
        <div className="max-h-[380px] overflow-y-auto px-4 py-2">
          {barSubset.length === 0 && (
            <div className="py-8 text-center text-xs text-slate-400">No data</div>
          )}
          {barSubset.map((r) => (
            <BarRow key={r.key} label={r.label} value={r.total} max={max}
              share={r.share_pct} tone={tone}
              to={buildTo && buildTo(r)}
              testid={`${testid}-bar-${r.key}`} />
          ))}
        </div>
      </div>
    </Card>
  );
}

// ─── Accessories Report (Location × DSE) ──────────────────────────────
function AccessoriesSection({ data }) {
  if (!data) return null;
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  return (
    <Card className="border-slate-200 p-5" data-testid="rep-accessories">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-2.5">
          <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md bg-amber-100 text-amber-700">
            <ShoppingBag className="h-4 w-4" />
          </span>
          <div>
            <div className="font-display text-base font-semibold text-slate-900">
              Accessories Sold — Location &amp; DSE
            </div>
            <div className="text-xs text-slate-500">
              Fitments captured during the sales form, aggregated by branch and by executive.
              Amounts are net of per-item discounts.
            </div>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <MiniStat label="Sales w/ Acc." value={data.sales_with_accessories} tone="slate" />
          <MiniStat label="Units Sold"   value={data.total_qty} tone="slate" />
          <MiniStat label="Revenue"      value={inr(data.total_amount)} tone="emerald" />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <AccessoriesTable
          title="Per Location"
          icon={MapPin}
          rows={data.locations}
          rowLabel={(r) => `${r.location_code} · ${r.location_name}`}
          testid="rep-acc-loc"
        />
        <AccessoriesTable
          title="Per DSE"
          icon={Users}
          rows={data.dse}
          rowLabel={(r) => r.dse_name}
          rowSubLabel={(r) => r.location_name}
          testid="rep-acc-dse"
        />
      </div>
    </Card>
  );
}

function MiniStat({ label, value, tone = "slate" }) {  const toneCls = tone === "emerald"
    ? "bg-emerald-50 text-emerald-800 ring-emerald-200"
    : "bg-slate-50 text-slate-800 ring-slate-200";
  return (
    <div className={cn(
      "rounded-md px-2.5 py-1.5 text-center ring-1",
      toneCls,
    )}>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</div>
      <div className="font-mono-data text-sm font-bold">{value}</div>
    </div>
  );
}

// ─── Accounts Reports (Excel-only downloads) ──────────────────────────────
// Spec: Reports → Accounts Reports → Sales Accounts Audit Tracker. Accounts
// downloads the workbook and works entirely offline — no data persists back
// to OneDesk. Role-gated to Admin / Accounts / SalesManager (view-only).
function AccountsReportsSection({ params, activeUser }) {
  const [downloading, setDownloading] = useState(false);
  const allowed = ["Admin", "Accounts", "SalesManager"].includes(activeUser?.role);
  if (!allowed) return null;

  const download = async (pendingOnly = false) => {
    setDownloading(true);
    try {
      const qs = new URLSearchParams();
      Object.entries(params || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") qs.append(k, v);
      });
      if (pendingOnly) qs.set("include_pending_only", "true");
      const token = authToken();
      if (token) qs.set("t", token);
      const url = `${API}/reports/sales_accounts_audit.xlsx?${qs.toString()}`;
      const a = document.createElement("a");
      a.href = url;
      a.download = `OneDesk360_Sales_Accounts_Audit_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success("Sales Accounts Audit — Excel download started");
    } catch (e) {
      toast.error(`Download failed: ${e?.message || e}`);
    } finally {
      setTimeout(() => setDownloading(false), 800);
    }
  };

  return (
    <Card className="border-slate-200 p-5" data-testid="rep-accounts-reports">
      <div className="mb-3 flex items-start gap-2.5">
        <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-100 text-emerald-700">
          <ClipboardCheck className="h-4 w-4" />
        </span>
        <div>
          <div className="font-display text-base font-semibold text-slate-900">
            Accounts Reports
          </div>
          <div className="text-xs text-slate-500">
            Downloadable Excel workbooks for offline audit &amp; reconciliation. All filters at the
            top are applied to the export.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-emerald-50/60 to-white p-4"
             data-testid="rep-tile-sales-accounts-audit">
          <div className="font-display text-sm font-bold uppercase tracking-wide text-slate-900">
            Sales Accounts Audit Tracker
          </div>
          <div className="mt-1 text-[11px] text-slate-500">
            One row per released sale · 47 columns · financial totals + payment / DDA / VAS /
            accessories status. Audit fields left blank for the team to fill inside Excel.
          </div>
          <div className="mt-2 flex flex-wrap gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            <span className="rounded bg-slate-100 px-1.5 py-0.5">Excel · .xlsx</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5">Colour-coded status</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5">Summary sheet</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              onClick={() => download(false)}
              disabled={downloading}
              className="h-9 gap-1.5"
              data-testid="rep-audit-download-full"
            >
              <Download className="h-4 w-4" />
              {downloading ? "Preparing…" : "Download Excel"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => download(true)}
              disabled={downloading}
              className="h-9 gap-1.5"
              data-testid="rep-audit-download-pending"
            >
              <Download className="h-4 w-4" />
              Pending Cases Only
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
}


function AccessoriesTable({ title, icon: Icon, rows, rowLabel, rowSubLabel, testid }) {
  const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
  return (
    <div className="rounded-lg border border-slate-200" data-testid={testid}>
      <div className="flex items-center gap-2 border-b border-slate-200 bg-slate-50 px-3 py-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-500" />}
        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
      </div>
      {!rows || rows.length === 0 ? (
        <div className="px-3 py-6 text-center text-xs text-slate-400">No accessories sold in this period.</div>
      ) : (
        <div className="max-h-[380px] overflow-y-auto">
          <table className="min-w-full text-xs">
            <thead className="sticky top-0 bg-white text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Name</th>
                <th className="px-2 py-2 text-right">Sales</th>
                <th className="px-2 py-2 text-right">Units</th>
                <th className="px-3 py-2 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={r.location_id || r.dse_id || i}
                    data-testid={`${testid}-row-${r.location_id || r.dse_id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-800">{rowLabel(r)}</div>
                    {rowSubLabel && (
                      <div className="text-[10px] text-slate-500">{rowSubLabel(r)}</div>
                    )}
                    {r.top_items && r.top_items.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1">
                        {r.top_items.slice(0, 3).map((it) => (
                          <span key={it.name}
                            className="inline-flex items-center gap-1 rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 ring-1 ring-amber-100">
                            {it.name} <span className="text-amber-700">×{it.qty}</span>
                          </span>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right font-mono-data">{r.sales_with_accessories}</td>
                  <td className="px-2 py-2 text-right font-mono-data">{r.total_qty}</td>
                  <td className="px-3 py-2 text-right font-mono-data font-semibold text-emerald-700">
                    {inr(r.total_amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─────────────────────── Main Page ───────────────────────

export default function Reports() {
  const { activeUser, locations, users } = useApp();
  const [rangeKey, setRangeKey] = useState("this_month");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const [filters, setFilters] = useState({
    location_id: null, dse_id: null, buy_mode: null,
    finance_company_id: null, model: null,
  });
  const [financeCompanies, setFinanceCompanies] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [report, setReport] = useState(null);
  const [accReport, setAccReport] = useState(null);
  const [exporting, setExporting] = useState(false);

  const canView = activeUser?.role === "Admin" || activeUser?.role === "SalesManager";
  const range = useMemo(() => rangeFor(rangeKey, customRange), [rangeKey, customRange]);

  const params = useMemo(() => {
    const p = { ...range };
    if (filters.location_id) p.location_ids = filters.location_id;
    if (filters.dse_id) p.dse_id = filters.dse_id;
    if (filters.buy_mode) p.buy_mode = filters.buy_mode;
    if (filters.finance_company_id) p.finance_company_id = filters.finance_company_id;
    if (filters.model) p.model = filters.model;
    if (filters.district) p.district = filters.district;
    if (filters.state) p.state = filters.state;
    return p;
  }, [range, filters]);

  // Bootstrap finance-company + model options (small master lists).
  useEffect(() => {
    if (!canView) return;
    (async () => {
      const [fc, mdl] = await Promise.all([
        Masters.list({ type: "finance_company" }),
        Masters.list({ type: "model" }),
      ]);
      setFinanceCompanies(fc || []);
      setModelOptions((mdl || []).map((m) => m.name));
    })();
  }, [canView]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    (async () => {
      const [r, acc] = await Promise.all([
        api.get("/reports/sales_analysis", { params }).then((x) => x.data),
        api.get("/reports/accessories", { params }).then((x) => x.data),
      ]);
      if (!cancelled) {
        setReport(r);
        setAccReport(acc);
      }
    })();
    return () => { cancelled = true; };
  }, [canView, params]);

  const onExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/reports/sales_analysis.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `OneDesk360_Sales_Analysis_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } finally { setExporting(false); }
  };

  // Build a /sales URL preserving current filters + a drill-down override.
  // We pin `date_field=submitted_at` so the drill-down count matches what the
  // report shows (Reports counts sales by release date, not creation date).
  const driveTo = (extra = {}) => {
    const merged = { date_field: "submitted_at", ...filters, ...extra, ...range };
    const qs = Object.entries(merged)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    return `/sales?${qs}`;
  };

  if (!activeUser) return null;
  if (!canView) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-center">
        <p className="text-sm font-medium text-amber-800">
          Reports are available to <strong>Admin</strong> and <strong>Sales Manager</strong> users only.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Mobile access block — Reports is desktop-only per spec */}
      <div className="lg:hidden">
        <Card className="border-amber-200 bg-amber-50 p-6 text-center" data-testid="reports-mobile-block">
          <div className="flex flex-col items-center gap-2">
            <Info className="h-6 w-6 text-amber-600" />
            <div className="font-display text-lg font-semibold text-amber-900">Reports are desktop-only</div>
            <p className="max-w-sm text-sm text-amber-800">
              For the best experience please open Reports on a laptop or desktop. The dashboards use dense tables and charts that don&apos;t render well on a phone screen.
            </p>
          </div>
        </Card>
      </div>

      <div className="hidden space-y-6 lg:block" data-testid="reports-page">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">Insights</div>
            <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-slate-900">
              Sales Analysis
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Slice retail performance by buy mode, finance company, DSE, model, location and district.
              Click any value to drill straight into the underlying sales list.
            </p>
          </div>
          <RangeBar value={rangeKey} onChange={setRangeKey} custom={customRange}
            onCustomChange={(c) => { setRangeKey("custom"); setCustomRange(c); }} />
        </div>

        <FilterBar
          locations={locations}
          users={users}
          filters={filters}
          setFilters={setFilters}
          financeCompanies={financeCompanies}
          modelOptions={modelOptions}
          onExport={onExport}
          exporting={exporting}
        />

        {/* Ask AI — sits directly under filters so it's the first thing users
            see on Reports. Answers are role-scoped by the backend. */}
        <AskAiSection canInsights />

        {!report ? (
          <div className="rounded-lg border border-slate-200 bg-white px-6 py-12 text-center text-sm text-slate-500">
            Loading…
          </div>
        ) : (
          <>
            <CashVsFinance report={report} driveTo={driveTo} />

            <AccessoriesSection data={accReport} />

            {/* Detail sections — 2-column grid on wide screens. Each card scrolls
                internally so the whole page doesn't grow indefinitely. */}
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <FinanceCompanySection
                rows={report.finance_companies}
                driveTo={driveTo}
              />

              <div className="xl:col-span-2">
                <TableBarSection
                  testid="rep-dse"
                  title="DSE Sales Performance"
                  subtitle="Retail leaderboard with VAS penetration % and accessories revenue per executive."
                  icon={Trophy}
                  tone="amber"
                  rows={report.dse}
                  rankTop3
                  buildTo={(r) => driveTo({ dse_id: r.key })}
                  columns={[
                    { key: "rank", label: "#", numeric: true },
                    { key: "label", label: "DSE",
                      render: (r) => (
                        <Link to={driveTo({ dse_id: r.key })} className="font-semibold text-slate-800 hover:text-primary">{r.label}</Link>
                      ) },
                    { key: "location_name", label: "Location",
                      render: (r) => <span className="text-slate-600">{r.location_name || "—"}</span> },
                    { key: "total", label: "Sold", numeric: true },
                    { key: "vas_amc", label: "AMC", numeric: true,
                      render: (r) => (
                        <span className="font-mono-data">
                          {r.vas_amc}
                          <span className="ml-1 text-[10px] font-medium text-slate-500">{r.vas_amc_pct}%</span>
                        </span>
                      ) },
                    { key: "vas_pp", label: "PP", numeric: true,
                      render: (r) => (
                        <span className="font-mono-data">
                          {r.vas_pp}
                          <span className="ml-1 text-[10px] font-medium text-slate-500">{r.vas_pp_pct}%</span>
                        </span>
                      ) },
                    { key: "vas_rsa", label: "RSA", numeric: true,
                      render: (r) => (
                        <span className="font-mono-data">
                          {r.vas_rsa}
                          <span className="ml-1 text-[10px] font-medium text-slate-500">{r.vas_rsa_pct}%</span>
                        </span>
                      ) },
                    { key: "accessories_amount", label: "Acc ₹", numeric: true,
                      render: (r) => (
                        <span className="font-mono-data text-slate-800">
                          {"\u20B9" + Math.round(r.accessories_amount).toLocaleString("en-IN")}
                        </span>
                      ) },
                    { key: "accessories_avg", label: "Avg/veh", numeric: true,
                      render: (r) => (
                        <span className="font-mono-data text-slate-500">
                          {"\u20B9" + Math.round(r.accessories_avg).toLocaleString("en-IN")}
                        </span>
                      ) },
                  ]}
                />
              </div>

              <TableBarSection
                testid="rep-models"
                title="Model-wise Sales"
                subtitle="Which Yamaha models are moving the fastest."
                icon={Bike}
                tone="emerald"
                rows={report.models}
                buildTo={(r) => driveTo({ model: r.key })}
                columns={[
                  { key: "label", label: "Model",
                    render: (r) => (
                      <Link to={driveTo({ model: r.key })} className="font-semibold text-slate-800 hover:text-primary">{r.label}</Link>
                    ) },
                  { key: "total", label: "Sold", numeric: true },
                  { key: "share_pct", label: "%", numeric: true,
                    render: (r) => `${r.share_pct}%` },
                ]}
              />

              <TableBarSection
                testid="rep-locations"
                title="Location-wise Sales"
                subtitle="Retail comparison across dealership locations, with VAS penetration."
                icon={MapPin}
                tone="sky"
                rows={report.locations}
                buildTo={(r) => driveTo({ location_id: r.key })}
                columns={[
                  { key: "label", label: "Location",
                    render: (r) => (
                      <Link to={driveTo({ location_id: r.key })} className="font-semibold text-slate-800 hover:text-primary">{r.label}</Link>
                    ) },
                  { key: "total", label: "Sold", numeric: true },
                  { key: "vas_amc", label: "AMC", numeric: true },
                  { key: "vas_pp",  label: "PP",  numeric: true },
                  { key: "vas_rsa", label: "RSA", numeric: true },
                ]}
              />

              {/* Location × Districts — spans both columns.
                  For every location, shows the customer districts those sales
                  came from, sorted by volume. */}
              <div className="xl:col-span-2">
                <LocationDistrictsCard rows={report.location_districts} driveTo={driveTo} />
              </div>

              {/* Accounts Reports — Excel-only downloadable tracker for the
                  Accounts team. Role-gated inside the component. */}
              <div className="xl:col-span-2">
                <AccountsReportsSection params={params} activeUser={activeUser} />
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
